/**
 * TIER 1 — PRIMITIVOS
 * ---------------------------------------------------------------------------
 * Valores crudos, sin significado de negocio. Ningún componente debe
 * consumirlos directamente: siempre a través de los tokens semánticos
 * (tier 2) o de dominio (tier 3).
 *
 * Fuente única de verdad para Web (Tailwind/CSS vars) y Móvil (RN/NativeWind).
 */

// ── Rampas de color ─────────────────────────────────────────────────────────
// 11 pasos (50..950). El 500 es el tono "base" de cada familia.

/**
 * Neutro "Niebla": gris apenas frío, casi sin color propio.
 *
 * La versión anterior llevaba una deriva verde para "armonizar con la marca".
 * En la práctica hacía lo contrario: sobre superficies grandes el verde se
 * acumula y el blanco deja de leerse como blanco — parece un blanco sucio o
 * una pantalla mal calibrada. En una herramienta donde el 90 % de los píxeles
 * son chrome, el neutro tiene que desaparecer, no participar.
 *
 * La identidad la lleva el verde de marca en los acentos, que es como trabajan
 * las herramientas de datos que mejor envejecen: chrome neutro, color con
 * intención.
 */
export const neutral = {
  0: '#FFFFFF',
  50: '#FAFAFB',
  100: '#F3F4F6',
  200: '#E6E7EB',
  300: '#CDD0D6',
  400: '#9CA0A8',
  500: '#6E727C',
  600: '#52565F',
  700: '#3D4048',
  800: '#292B31',
  900: '#17181C',
  950: '#0D0E11',
  1000: '#000000',
} as const;

/** Marca "Campo": verde agrícola profundo. Identidad + acciones primarias. */
export const green = {
  50: '#ECFAF1',
  100: '#D2F3DF',
  200: '#A6E6C1',
  300: '#71D29E',
  400: '#3FB87C',
  500: '#1E9C60',
  600: '#157D4D',
  700: '#12633E',
  800: '#124F33',
  900: '#10412C',
  950: '#062518',
} as const;

/** Acento "Cosecha": ámbar/oro. Destacados, estados en curso, marca secundaria. */
export const amber = {
  50: '#FEF8EA',
  100: '#FDECC4',
  200: '#FBD989',
  300: '#F8C24D',
  400: '#F5AD23',
  500: '#E5940D',
  600: '#C4720A',
  700: '#9C520D',
  800: '#804111',
  900: '#6C3612',
  950: '#3F1B06',
} as const;

export const red = {
  50: '#FEF2F2',
  100: '#FEE2E2',
  200: '#FECACA',
  300: '#FCA5A5',
  400: '#F87171',
  500: '#EF4444',
  600: '#DC2626',
  700: '#B91C1C',
  800: '#991B1B',
  900: '#7F1D1D',
  950: '#450A0A',
} as const;

export const blue = {
  50: '#EFF6FF',
  100: '#DBEAFE',
  200: '#BFDBFE',
  300: '#93C5FD',
  400: '#60A5FA',
  500: '#3B82F6',
  600: '#2563EB',
  700: '#1D4ED8',
  800: '#1E40AF',
  900: '#1E3A8A',
  950: '#172554',
} as const;

export const violet = {
  50: '#F5F3FF',
  100: '#EDE9FE',
  200: '#DDD6FE',
  300: '#C4B5FD',
  400: '#A78BFA',
  500: '#8B5CF6',
  600: '#7C3AED',
  700: '#6D28D9',
  800: '#5B21B6',
  900: '#4C1D95',
  950: '#2E1065',
} as const;

export const teal = {
  50: '#F0FDFA',
  100: '#CCFBF1',
  200: '#99F6E4',
  300: '#5EEAD4',
  400: '#2DD4BF',
  500: '#14B8A6',
  600: '#0D9488',
  700: '#0F766E',
  800: '#115E59',
  900: '#134E4A',
  950: '#042F2E',
} as const;

export const orange = {
  50: '#FFF7ED',
  100: '#FFEDD5',
  200: '#FED7AA',
  300: '#FDBA74',
  400: '#FB923C',
  500: '#F97316',
  600: '#EA580C',
  700: '#C2410C',
  800: '#9A3412',
  900: '#7C2D12',
  950: '#431407',
} as const;

export const pink = {
  50: '#FDF2F8',
  100: '#FCE7F3',
  200: '#FBCFE8',
  300: '#F9A8D4',
  400: '#F472B6',
  500: '#EC4899',
  600: '#DB2777',
  700: '#BE185D',
  800: '#9D174D',
  900: '#831843',
  950: '#500724',
} as const;

export const palette = {
  neutral,
  green,
  amber,
  red,
  blue,
  violet,
  teal,
  orange,
  pink,
} as const;

// ── Espaciado ───────────────────────────────────────────────────────────────
// Base 4px. Las claves son múltiplos del base salvo `px` y las medias.
export const space = {
  0: 0,
  px: 1,
  0.5: 2,
  1: 4,
  1.5: 6,
  2: 8,
  2.5: 10,
  3: 12,
  3.5: 14,
  4: 16,
  5: 20,
  6: 24,
  7: 28,
  8: 32,
  9: 36,
  10: 40,
  11: 44,
  12: 48,
  14: 56,
  16: 64,
  20: 80,
  24: 96,
  28: 112,
  32: 128,
} as const;

// ── Radios ──────────────────────────────────────────────────────────────────
export const radius = {
  none: 0,
  xs: 4,
  sm: 6,
  md: 10,
  lg: 14,
  xl: 20,
  '2xl': 28,
  '3xl': 36,
  full: 9999,
} as const;

// ── Tipografía ──────────────────────────────────────────────────────────────
export const fontFamily = {
  sans: ['Inter', 'Inter var', 'system-ui', '-apple-system', 'Segoe UI', 'Roboto', 'sans-serif'],
  display: ['Inter', 'Inter var', 'system-ui', '-apple-system', 'Segoe UI', 'Roboto', 'sans-serif'],
  mono: ['JetBrains Mono', 'ui-monospace', 'SFMono-Regular', 'Menlo', 'Consolas', 'monospace'],
} as const;

/** Nombres de fuente tal cual se registran en Expo (expo-font / @expo-google-fonts). */
export const nativeFontFamily = {
  regular: 'Inter_400Regular',
  medium: 'Inter_500Medium',
  semibold: 'Inter_600SemiBold',
  bold: 'Inter_700Bold',
  mono: 'JetBrainsMono_400Regular',
} as const;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

/** Escala tipográfica: tamaño px, alto de línea px, tracking em. */
export const fontSize = {
  '2xs': { size: 10, lineHeight: 14, letterSpacing: 0.02 },
  xs: { size: 12, lineHeight: 16, letterSpacing: 0.01 },
  sm: { size: 14, lineHeight: 20, letterSpacing: 0 },
  base: { size: 16, lineHeight: 24, letterSpacing: 0 },
  lg: { size: 18, lineHeight: 28, letterSpacing: -0.005 },
  xl: { size: 20, lineHeight: 28, letterSpacing: -0.01 },
  '2xl': { size: 24, lineHeight: 32, letterSpacing: -0.015 },
  '3xl': { size: 30, lineHeight: 38, letterSpacing: -0.02 },
  '4xl': { size: 36, lineHeight: 44, letterSpacing: -0.02 },
  '5xl': { size: 48, lineHeight: 56, letterSpacing: -0.025 },
} as const;

/** Roles tipográficos listos para usar (mapean a la escala de arriba). */
export const typeRole = {
  displayLg: { size: '4xl', weight: 'bold' },
  displayMd: { size: '3xl', weight: 'bold' },
  displaySm: { size: '2xl', weight: 'semibold' },
  headingLg: { size: 'xl', weight: 'semibold' },
  headingMd: { size: 'lg', weight: 'semibold' },
  headingSm: { size: 'base', weight: 'semibold' },
  bodyLg: { size: 'base', weight: 'regular' },
  bodyMd: { size: 'sm', weight: 'regular' },
  bodySm: { size: 'xs', weight: 'regular' },
  label: { size: 'sm', weight: 'medium' },
  caption: { size: 'xs', weight: 'medium' },
  overline: { size: '2xs', weight: 'semibold' },
  /** Cifras de KPI: siempre tabular para que no "bailen" al actualizarse. */
  metric: { size: '3xl', weight: 'bold' },
  metricSm: { size: 'xl', weight: 'bold' },
} as const;

// ── Elevación ───────────────────────────────────────────────────────────────
export const shadow = {
  none: 'none',
  xs: '0 1px 2px 0 rgba(13, 14, 17, 0.04)',
  sm: '0 1px 2px 0 rgba(13, 14, 17, 0.05), 0 1px 3px 0 rgba(13, 14, 17, 0.06)',
  md: '0 2px 4px -1px rgba(13, 14, 17, 0.05), 0 4px 8px -2px rgba(13, 14, 17, 0.06)',
  lg: '0 4px 8px -2px rgba(13, 14, 17, 0.06), 0 12px 20px -4px rgba(13, 14, 17, 0.08)',
  xl: '0 8px 16px -4px rgba(13, 14, 17, 0.07), 0 24px 40px -8px rgba(13, 14, 17, 0.10)',
  '2xl': '0 16px 32px -8px rgba(13, 14, 17, 0.09), 0 40px 64px -16px rgba(13, 14, 17, 0.14)',
  focus: '0 0 0 3px rgba(30, 156, 96, 0.32)',
} as const;

/** Equivalente nativo (iOS shadow* + Android elevation). */
export const nativeShadow = {
  none: { shadowColor: '#0D0E11', shadowOpacity: 0, shadowRadius: 0, shadowOffset: { width: 0, height: 0 }, elevation: 0 },
  xs: { shadowColor: '#0D0E11', shadowOpacity: 0.06, shadowRadius: 2, shadowOffset: { width: 0, height: 1 }, elevation: 1 },
  sm: { shadowColor: '#0D0E11', shadowOpacity: 0.1, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 2 },
  md: { shadowColor: '#0D0E11', shadowOpacity: 0.12, shadowRadius: 8, shadowOffset: { width: 0, height: 4 }, elevation: 4 },
  lg: { shadowColor: '#0D0E11', shadowOpacity: 0.14, shadowRadius: 16, shadowOffset: { width: 0, height: 8 }, elevation: 8 },
  xl: { shadowColor: '#0D0E11', shadowOpacity: 0.18, shadowRadius: 28, shadowOffset: { width: 0, height: 14 }, elevation: 16 },
  '2xl': { shadowColor: '#0D0E11', shadowOpacity: 0.22, shadowRadius: 44, shadowOffset: { width: 0, height: 22 }, elevation: 24 },
} as const;

// ── Movimiento ──────────────────────────────────────────────────────────────
export const duration = {
  instant: 0,
  fast: 120,
  base: 200,
  slow: 320,
  slower: 480,
} as const;

export const easing = {
  standard: 'cubic-bezier(0.2, 0, 0, 1)',
  decelerate: 'cubic-bezier(0, 0, 0, 1)',
  accelerate: 'cubic-bezier(0.3, 0, 1, 1)',
  emphasized: 'cubic-bezier(0.2, 0, 0, 1)',
  linear: 'linear',
} as const;

/** Bézier crudo para Reanimated / Easing.bezier(...) en React Native. */
export const easingBezier = {
  standard: [0.2, 0, 0, 1],
  decelerate: [0, 0, 0, 1],
  accelerate: [0.3, 0, 1, 1],
  emphasized: [0.2, 0, 0, 1],
} as const;

/** Configuración de resorte para react-native-reanimated. */
export const spring = {
  gentle: { damping: 20, stiffness: 180, mass: 1 },
  snappy: { damping: 26, stiffness: 320, mass: 1 },
  bouncy: { damping: 14, stiffness: 200, mass: 1 },
} as const;

// ── Layout ──────────────────────────────────────────────────────────────────
export const breakpoint = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export const zIndex = {
  base: 0,
  raised: 10,
  dropdown: 1000,
  sticky: 1100,
  overlay: 1200,
  modal: 1300,
  popover: 1400,
  toast: 1500,
  tooltip: 1600,
} as const;

/**
 * Alturas de control. `web` y `native` difieren a propósito: en móvil el
 * mínimo táctil accesible es 44pt (HIG) / 48dp (Material).
 */
export const controlHeight = {
  xs: { web: 28, native: 36 },
  sm: { web: 32, native: 40 },
  md: { web: 40, native: 48 },
  lg: { web: 48, native: 56 },
} as const;

export const size = {
  /** Objetivo táctil mínimo en móvil. */
  minTouchTarget: 44,
  /** Ancho del sidebar de escritorio expandido / colapsado. */
  sidebarExpanded: 264,
  sidebarCollapsed: 72,
  /** Alto de la barra superior. */
  topbar: 60,
  /** Alto de la tab bar nativa (sin safe-area). */
  tabbar: 56,
  /** Ancho máximo de contenido. */
  contentMax: 1440,
  proseMax: 720,
  /** Alto de fila en tablas. */
  tableRow: 52,
  tableRowCompact: 40,
  avatarXs: 24,
  avatarSm: 32,
  avatarMd: 40,
  avatarLg: 56,
  avatarXl: 80,
  iconXs: 14,
  iconSm: 16,
  iconMd: 20,
  iconLg: 24,
  iconXl: 32,
} as const;

export const opacity = {
  disabled: 0.45,
  muted: 0.65,
  hover: 0.08,
  pressed: 0.14,
  scrim: 0.55,
} as const;

export const borderWidth = {
  0: 0,
  hairline: 1,
  thin: 1,
  medium: 2,
  thick: 3,
} as const;
